import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, Sparkles, Wand2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
} from "@eduviaos/ui-kit";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { moduleByRoute } from "@/lib/moduleCatalog";
import { getVerticalProfile, moduleNavLabelFor } from "@/lib/verticalProfile";

/**
 * SC Foundation (F0) — on-brand, vertical-aware "in progress" screen for
 * modules that are enabled for the tenant but whose surface hasn't
 * shipped yet (`empty_states[key] === "not_implemented"`).
 *
 * It reads vertical-specific copy from the vertical profile so a study
 * centre sees "…for your centre" language and an AI-assist teaser rather
 * than school-shaped filler. For DISABLED modules the AppShell hides the
 * nav link and this component redirects to /dashboard, so if it renders,
 * the module IS enabled — just not finished.
 */
export function ModuleComingSoonPage() {
  const { pathname } = useLocation();
  const { isModuleEnabled, isLoading, vertical } = useEnabledModules();
  const moduleDescriptor = moduleByRoute(pathname);

  // One-shot mount reveal (rare screen → a small orchestrated entrance is
  // appropriate). transform + opacity only; disabled under reduced motion.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!moduleDescriptor) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <div className="bg-muted h-7 w-40 animate-pulse rounded" />
        <div className="bg-muted mt-3 h-4 w-72 animate-pulse rounded" />
      </div>
    );
  }

  if (!isModuleEnabled(moduleDescriptor.key)) {
    return <Navigate to="/dashboard" replace />;
  }

  const profile = vertical ? getVerticalProfile(vertical) : undefined;
  const copy = profile?.moduleCopy[moduleDescriptor.key];
  const blurb = copy?.blurb ?? moduleDescriptor.description;
  const aiTeaser = copy?.aiTeaser;
  const Icon = moduleDescriptor.icon;

  const statusLabel = copy
    ? `In progress — coming to your ${profile?.institutionNoun.toLowerCase() ?? "workspace"}`
    : moduleDescriptor.plannedPhase
      ? `Planned for Phase ${moduleDescriptor.plannedPhase}`
      : "In progress";

  return (
    <div className="mx-auto max-w-2xl py-12">
      <Button variant="ghost" size="sm" asChild className="mb-6">
        <Link to="/dashboard">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to dashboard
        </Link>
      </Button>

      <Card
        className={cn(
          "border-border/80 bg-card/60 backdrop-blur",
          "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none",
          entered ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
        )}
        style={{ transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)" }}
      >
        <CardHeader className="flex-col items-start gap-3 pb-4">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary grid size-12 place-items-center rounded-xl"
            >
              <Icon className="size-6" />
            </span>
            <div>
              <CardTitle className="text-2xl tracking-tight">
                {profile
                  ? moduleNavLabelFor(profile, moduleDescriptor.key, moduleDescriptor.label)
                  : moduleDescriptor.label}
              </CardTitle>
              <CardDescription className="mt-1 inline-flex items-center gap-1.5 text-xs">
                <Sparkles className="size-3.5" aria-hidden="true" />
                {statusLabel}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-foreground/80 text-sm leading-relaxed">{blurb}</p>

          {aiTeaser ? (
            <div className="border-primary/20 bg-primary/5 flex items-start gap-3 rounded-lg border p-4">
              <span
                aria-hidden="true"
                className="bg-primary/10 text-primary mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg"
              >
                <Wand2 className="size-4" />
              </span>
              <div>
                <p className="text-sm font-medium">Built-in AI assist</p>
                <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{aiTeaser}</p>
              </div>
            </div>
          ) : null}

          <div className="border-border bg-muted/30 rounded-lg border p-4 text-sm">
            <p className="font-medium">What's next</p>
            <p className="text-muted-foreground mt-1 text-xs">
              We're building this surface now. When it ships it'll appear right here — no setup
              needed on your end. Your already-live modules keep working in the meantime.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button asChild variant="default" size="sm">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
